// KasperLanding.tsx
import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  Grid,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import DescriptionIcon from '@mui/icons-material/Description';

const KasperLanding = () => {
  return (
    <Box sx={{ bgcolor: '#f9fafb', py: 8 }}>
      <Container maxWidth="md">
        <Stack spacing={6}>
          {/* Hero Section */}
          <Box textAlign="center">
            <RocketLaunchIcon fontSize="large" color="primary" />
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Meet Kasper
            </Typography>
            <Typography variant="h6" color="text.secondary" maxWidth="md" mx="auto">
              Kasper is a lightweight app to help developers send test events to a Kafka instance quickly, without needing to code every time.
            </Typography>
          </Box>

          {/* How it Helps */}
          <Card elevation={3} sx={{ borderRadius: 4 }}>
            <CardContent>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Why Use Kasper?
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <AutoAwesomeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="No need to write scripts to test Kafka"
                    secondary="Build event 'tests' through the interface, no Python or CLI needed."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DescriptionIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Work with JSON files directly"
                    secondary="Events are stored as JSON, easy to version, edit, or use in other tools."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="All your test events in one place"
                    secondary="Keep Kafka test data organized and reusable across projects."
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* How it Works */}
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              How it Works
            </Typography>
            <Grid container spacing={2}>
              {[
                {
                  title: '1. Launch Kasper',
                  desc: 'Run the .exe file – no setup, just open and go.',
                },
                {
                  title: '2. Create Test Events',
                  desc: 'Use the UI to create test Kafka events with keys and values.',
                },
                {
                  title: '3. Send to Kafka',
                  desc: 'Choose your topic and send the events to your Kafka instance.',
                },
              ].map((step, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Card sx={{ borderRadius: 3, p: 2, textAlign: 'center', height: '100%' }}>
                    <Typography variant="h6" fontWeight="medium">
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.desc}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Download Section */}
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Ready to Try It?
            </Typography>
            <Button
              variant="contained"
              size="large"
              color="primary"
              startIcon={<CloudDownloadIcon />}
              sx={{ px: 5, py: 1.5, fontWeight: 600 }}
              href="https://github.com/JmJDrWrk/KafkaEventSenderApp/raw/refs/heads/main/KasperApp_0.1.1.exe" // <-- update this
            >
              Download Kasper.exe
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              No sign-up required. Just run the EXE and start testing.
            </Typography>
          </Box>

          {/* Footer Note */}
          <Box textAlign="center" pt={4} color="text.secondary">
            <CodeIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            <Typography variant="caption" component="span">
              Source code is private — free version available temporarily
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default KasperLanding;
